import { useContext, useEffect, useState } from "react";
import { DndContext, PointerSensor, TouchSensor, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { MainContext } from "../App";
import Product from "./cart/Product";
import functions from "../utilities/functions";
import { getSelectedVariantsPrice } from "../utilities/selectedVariants";
import NoteIcon from "../icons/NoteIcon";
import CloseIcon from "../icons/CloseIcon";

function Cart() {
  const {
    table,
    list,
    emptyCart,
    checkout,
    loading,
    setAlertData,
    setPopupVisible,
    cart,
    setCart,
    flow,
    setFlow,
    maxFlow,
    settings,
    setCartVisible,
    updateOrderFromCart,
  } = useContext(MainContext);
  const [count, setCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [draggedProduct, setDraggedProduct] = useState(false);
  const [dropFlow, setDropFlow] = useState(false);
  const baseFlowsNumber = Math.max(parseInt(settings?.orders?.flows_number || 5), 1);
  const isFlowEnabled = (settings.orders?.enable_flow ?? settings.waiter_app?.enable_flow) == 1;
  const isCartEmpty = !cart?.products || cart.products.length === 0;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  useEffect(() => {
    sortProduts(cart);
    countProduts(cart);
  }, [cart, isFlowEnabled, maxFlow, baseFlowsNumber]);

  const sortProduts = (c) => {
    if (!c) return;
    if (!isFlowEnabled) {
      let singleFlow = {
        id: 1,
        name: "",
        products: [],
      };
      let i = 0;
      for (const p of c.products) {
        p.index = i;
        singleFlow.products.push(p);
        i++;
      }
      setProducts(singleFlow.products.length > 0 ? [singleFlow] : []);
      return;
    }
    let r = [];
    const maxProductFlow = c.products.length > 0 ? Math.max(...c.products.map((p) => parseInt(p.flow || 0))) : 0;
    const flowsLimit = Math.max(parseInt(maxFlow || baseFlowsNumber), maxProductFlow, baseFlowsNumber);
    for (let index = 1; index <= flowsLimit; index++) {
      let flowData = {
        id: index,
        name: "Portata " + index,
        products: [],
      };
      let i = 0;
      for (const p of c.products) {
        p.index = i;
        if (parseInt(p.flow) == index) flowData.products.push(p);
        i++;
      }
      r.push(flowData);
    }
    setProducts(r);
  };

  const countProduts = (c) => {
    if (!c) return;
    let t = 0;
    for (const e of c.products) {
      t += parseInt(e.qty);
    }
    setCount(t);
  };

  const handleCheckout = () => {
    if (count == 0 || loading) return;
    if (!cart.order_id) {
      setAlertData({
        title: "Confermi il tuo ordine?",
        message: "Clicca si per procedere",
        showCancelButton: true,
        callback: checkout,
      });
    } else {
      setAlertData({
        title: "Salvare le modifiche?",
        message: "Ordine " + cart.order_id,
        showCancelButton: true,
        callback: () => {
          updateOrderFromCart();
        },
      });
    }
  };

  const handleEmpty = () => {
    if (count == 0 || loading) return;
    setAlertData({
      title: "Sei sicuro?",
      message: "",
      showCancelButton: true,
      callback: emptyCart,
    });
  };

  const getCartTotal = () => {
    let t = 0;
    for (const product of cart.products) {
      let price = parseFloat(product.price);
      price += getSelectedVariantsPrice(product.selectedVariants);
      t += parseInt(product.qty) * price;
    }
    return t;
  };

  const handleFlowDragStart = (event) => {
    const payload = event.active?.data?.current;
    if (!payload) return;
    setDraggedProduct({
      index: payload.index,
      flow: parseInt(payload.flow),
    });
  };

  const handleFlowDragEnd = () => {
    setDraggedProduct(false);
    setDropFlow(false);
  };

  const handleFlowDragOver = (event) => {
    if (!draggedProduct) return;
    const overId = event.over?.id;
    if (!overId) return;
    const overFlowId = parseInt(String(overId).replace("flow-drop-", ""));
    if (dropFlow !== overFlowId) setDropFlow(overFlowId);
  };

  const handleFlowDrop = (event) => {
    const overId = event.over?.id;
    const dragData = event.active?.data?.current || draggedProduct;
    if (!dragData || !overId) {
      handleFlowDragEnd();
      return;
    }

    const targetFlow = parseInt(String(overId).replace("flow-drop-", ""));
    const sourceIndex = dragData.index;
    if (sourceIndex === false || sourceIndex === undefined) {
      handleFlowDragEnd();
      return;
    }
    if (parseInt(cart.products[sourceIndex]?.flow) === targetFlow) {
      handleFlowDragEnd();
      return;
    }

    let tempCart = { ...cart, products: [...cart.products] };
    tempCart.products[sourceIndex] = { ...tempCart.products[sourceIndex], flow: targetFlow };
    setCart(tempCart);
    handleFlowDragEnd();
  };

  return (
    <div className="popup md cart-popup">
      <div className="container cart-popup-container">
        <header>
          <div className="buttons-group">
            <button className="btn btn-tertiary btn-square" onClick={() => setCartVisible(false)}>
              <CloseIcon />
            </button>
            <div>
              <div>Tavolo: {table ? table.name : "nessuno"}</div>
              <div className="extrasmall">Listino: {table?.list_id ? table?.list?.name || "Base" : "Base"}</div>
            </div>
          </div>
          <div className="buttons-group">
            <button
              className="btn btn-tertiary btn-square cart-notes-trigger"
              onClick={() => setPopupVisible("orderNotes")}
              aria-label="Apri note ordine"
            >
              <NoteIcon />
            </button>
          </div>
        </header>
        <DndContext
          sensors={sensors}
          onDragStart={handleFlowDragStart}
          onDragOver={handleFlowDragOver}
          onDragEnd={handleFlowDrop}
          onDragCancel={handleFlowDragEnd}
        >
          <div className="content cart-content">
            {isCartEmpty ? (
              <div className="cart-empty-state">Nessun prodotto presente nel carrello</div>
            ) : (
              products.map((f, n) => (
                <FlowDropArea key={n} flowId={f.id} isEnabled={isFlowEnabled} isEmpty={f.products.length === 0}>
                  {isFlowEnabled && (
                    <button className={f.id == flow ? "list-title selected" : "list-title"} onClick={() => setFlow(f.id)}>
                      {f.name}
                    </button>
                  )}
                  {f.products.map((product, i) => (
                    <Product
                      product={product}
                      key={i}
                      isFlowDragEnabled={isFlowEnabled}
                      dragData={{
                        index: product.index,
                        flow: product.flow,
                      }}
                    />
                  ))}
                  {isFlowEnabled && draggedProduct && dropFlow === f.id && (
                    <div className="flow-drop-placeholder">Rilascia qui: Portata {f.id}</div>
                  )}
                </FlowDropArea>
              ))
            )}
            {cart.notes && (
              <div className="text-box cart-notes">
                Note:<div className="small">{cart.notes}</div>
              </div>
            )}
          </div>
        </DndContext>
        <footer className="buttons-group cart-footer">
          <div>{functions.formatEuro(getCartTotal())}</div>
          <button onClick={() => handleEmpty()} className="btn btn-tertiary btn-block" disabled={loading}>
            Svuota
          </button>
          <button onClick={() => handleCheckout()} className="btn btn-secondary btn-block" disabled={loading}>
            {cart.order_id ? "Salva" : "Invia"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function FlowDropArea({ flowId, isEnabled, isEmpty, children }) {
  const { isOver, setNodeRef } = useDroppable({
    id: "flow-drop-" + flowId,
    disabled: !isEnabled,
  });

  return (
    <div
      ref={isEnabled ? setNodeRef : undefined}
      className={
        "flow-drop-area " +
        (isEnabled ? "flow-drop-area-enabled " : "flow-drop-area-disabled ") +
        (isEnabled && isEmpty ? "flow-drop-empty " : "") +
        (isEnabled && isOver ? "flow-drop-target" : "")
      }
    >
      {children}
    </div>
  );
}

export default Cart;
